<template>
  <v-card
    class="
      chat-container
      rounded-lg
    "
    flat
  >
    <v-card-title
      class="grey lighten-2"
    >
      <h4>Notes</h4>

      <v-spacer />

      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn :loading="!collaboratorUuid" icon v-on="on" @click="toggleFullcreen">
            <v-icon :color="isFullscreen ? 'primary' : ''">
              {{ isFullscreen ? 'mdi-close' : 'mdi-overscan' }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{ tooltipText }}</span>
      </v-tooltip>
    </v-card-title>
    <v-skeleton-loader
      :loading="!collaboratorUuid"
      type="paragraph, paragraph, paragraph, paragraph, paragraph"
      class="px-4"
    >
      <v-card-text
        ref="messagesContainer"
        class="
          messages-container
          d-flex flex-column
          py-0
        "
        :class="{ 'mt-7': isFullscreen }"
        style="overflow-y: auto;"
        :style="isFullscreen ? 'min-height: 300px;' : 'height: 300px;'"
      >
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="
            message
            my-3
          "
          :class="message.collaborator ? getMessageAlignment(message) : ''"
        >
          <div
            v-if="message"
            class="
              rounded-lg
              pa-4
            "
            :class="message.collaborator ? getMessageClass(message) : ''"
            style="white-space: pre-line;"
            :style="message.collaborator ? '' : 'border: 1px solid #e0e0e0;'"
          >
            <p v-if="message.collaborator" class="caption font-weight-bold">
              {{ collaboratorName(message.collaborator) }} -
              <span class="font-weight-medium">{{ message.collaborator.email }}</span>
            </p>

            <span v-html="formatMessageWithLinks(message.text)"></span>

            <p
              class="
                mb-0
                caption text-right font-weight-bold
              "
            >
              {{ formatMessageTime(message.createTime) }}
            </p>
          </div>
        </div>
        <div ref="bottomAnchor"></div>
      </v-card-text>
    </v-skeleton-loader>

    <v-skeleton-loader
      :loading="!collaboratorUuid"
      type="image"
      class="pa-4"
    >
      <v-card-actions>
        <v-textarea
          v-model="newMessage"
          label="Saisir un message"
          outlined
          dense
          hide-details
          append-icon="mdi-send"
          :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
          @click:append="sendMessage"
        />
      </v-card-actions>
    </v-skeleton-loader>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';

import moment from 'moment';

export default {
  props: {
    collaboratorUuid: {
      type: String,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: null
    }
  },
  data () {
    return { newMessage: '' };
  },
  computed: {
    ...mapGetters(['canPerformAction']),
    collaboratorName () {
      return collaborator => `${collaborator.firstname} ${collaborator.lastname}`;
    },
    tooltipText () {
      return this.isFullscreen
        ? 'Quitter le mode plein écran'
        : 'Entrer dans le mode plein écran';
    }
  },
  watch: {
    messages: {
      handler () {
        this.scrollToBottom();
      },
      deep: true
    }
  },
  methods: {
    scrollToBottom () {
      this.$nextTick(() => {
        this.$vuetify.goTo(this.$refs.bottomAnchor, {
          container: this.$refs.messagesContainer,
          duration: 500,
          easing: 'easeInOutCubic'
        });
      });
    },
    getMessageAlignment (message) {
      return message.collaborator.uuid === this.collaboratorUuid
        ? 'align-self-end'
        : 'align-self-baseline';
    },
    getMessageClass (message) {
      return message.collaborator.uuid === this.collaboratorUuid
        ? 'primary white--text'
        : 'light-blue white--text';
    },
    sendMessage () {
      if (!this.newMessage.trim()) return;

      this.$emit('add-message', this.newMessage);

      this.newMessage = '';
    },
    toggleFullcreen () {
      this.scrollToBottom();
      this.$emit('toggle-fullscreen');
    },
    formatMessageTime (date) {
      moment.locale('fr');

      const messageDate = moment(date);

      if (!messageDate.isValid()) return '';

      if (messageDate.isSame(moment(), 'day')) {
        return messageDate.format('HH:mm');
      }

      return messageDate.format('DD/MM/YYYY HH:mm');
    },
    formatMessageWithLinks (text) {
      if (!text) return '';

      // URL regex pattern - improved to correctly match URLs
      const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(\/[^\s]*)?)/g;

      // Replace URLs with HTML anchor tags
      return text.replace(urlRegex, url => {
        // Add http:// if protocol is missing
        let href = url;
        if (!href.match(/^https?:\/\//i)) {
          href = `https://${href}`;
        }

        return `<a id="chat-link" href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
    }
  }
};
</script>

<style>
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.message {
  max-width: 100%;
}

#chat-link {
  color: white;
}
</style>
